import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Button, Grid } from '@material-ui/core';

import BaseDialog from 'core/components/base-dialog';
import Divider from 'core/components/divider';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import removeIcon from 'core/assets/svg/remove.svg';

import useStyles from './association-discount-modal.styles';

/**
 * The MO Farm Associations discount (CFM) is the one farm discount that is NOT a boolean: the
 * rater's `farm_association` answer is the COUNT of memberships
 * (cfm/raters/farm/answers-handlers/base-risk-answers.handler.ts), so applying it means recording
 * WHICH associations the insured belongs to, not just that the discount is on. That is why it needs
 * a modal instead of the plain add/remove row every other discount uses.
 *
 * Store-free and Formik-free on purpose: the offer sidebar and the policy Details card both apply
 * this discount and persist it differently (the offer also mirrors it into the MobX offer store),
 * so each owns its own `onApply`/`onRemove`. Before #34739 only the offer had it, and the policy
 * card's plain toggle wrote `global.farmAssociationDiscount` alone — which the rater never reads,
 * so a mid-term add printed "Included" on the dec while the premium never moved.
 */
const validate = (formValues, onError) => {
  const errors = {};
  if (!formValues.memberOfCattlemenAssociation && !formValues.memberOfSoybeanAssociation) {
    errors.memberOfCattlemenAssociation = 'At least one association must be selected';
    errors.memberOfSoybeanAssociation = 'At least one association must be selected';
    onError();
  }
  return errors;
};

const AssociationDiscountModal = ({ isActive, memberships, onApply, onRemove, onClose }) => {
  const classes = useStyles();
  const toast = useToast();

  const handleSubmit = useCallback(
    (modalValues) => {
      onApply({
        memberOfCattlemenAssociation: modalValues.memberOfCattlemenAssociation,
        memberOfSoybeanAssociation: modalValues.memberOfSoybeanAssociation
      });
      toast.notify({ type: 'success', message: 'The MO Farm Associations discount has been added.' });
      onClose();
    },
    [onApply, onClose, toast]
  );

  const handleRemove = useCallback(() => {
    onRemove();
    toast.notify({ type: 'success', message: 'The MO Farm Associations discount has been removed.' });
    onClose();
  }, [onRemove, onClose, toast]);

  return (
    <BaseDialog onClose={onClose} open>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Label type="modalTitle" className={classes.title}>
            MO Farm Associations Discount
          </Label>
        </Grid>
        <Grid item xs={12}>
          <Label type="greenSmall">
            A discount is available on owner-occupied policies if named insured is a member of the Cattlemen&apos;s
            Association or Soybean Association.
          </Label>
        </Grid>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
        </Grid>
        <Grid item xs={12} className={classes.formSubtitle}>
          <Label>Associations the member is a part of:</Label>
        </Grid>
        <Grid item xs={12}>
          <Formik
            initialValues={{
              memberOfCattlemenAssociation: memberships?.memberOfCattlemenAssociation ?? false,
              memberOfSoybeanAssociation: memberships?.memberOfSoybeanAssociation ?? false
            }}
            onSubmit={handleSubmit}
            validate={(formValues) =>
              validate(formValues, () =>
                toast.notify({ type: 'error', message: 'Please complete the required form items to continue.' })
              )
            }
            enableReinitialize
          >
            <Form>
              <Field
                xs={12}
                type="checkbox"
                id="memberOfCattlemenAssociation"
                name="memberOfCattlemenAssociation"
                label="Cattlemen's Association"
                mode="light"
              />
              <Field
                xs={12}
                type="checkbox"
                id="memberOfSoybeanAssociation"
                name="memberOfSoybeanAssociation"
                label="Soybean Association"
                mode="light"
              />
              <Grid container direction="row" justifyContent="flex-end" className={classes.buttonContainer}>
                {isActive && (
                  <Grid item>
                    <Button
                      onClick={handleRemove}
                      variant="text"
                      color="secondary"
                      className={classes.removeDiscountBtn}
                    >
                      <img alt="trashcan icon" src={removeIcon} className={classes.removeIcon} />
                      <Label variant="body3">Remove discount</Label>
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button className={classes.applyDiscountBtn} variant="contained" color="primary" type="submit">
                    Apply discount
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Formik>
        </Grid>
      </Grid>
    </BaseDialog>
  );
};

AssociationDiscountModal.propTypes = {
  isActive: PropTypes.bool,
  memberships: PropTypes.shape({
    memberOfCattlemenAssociation: PropTypes.bool,
    memberOfSoybeanAssociation: PropTypes.bool
  }),
  onApply: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

AssociationDiscountModal.defaultProps = {
  isActive: false,
  memberships: null
};

export default AssociationDiscountModal;
